import { PLATFORM_BILLING_API, adjustmentListSchema, adjustmentSchema, billingDocumentListSchema, billingExemptionSchema, contractListSchema, contractSchema, internalContractSummarySchema, periodUsageSchema, planVersionListSchema, planVersionSchema, ratedPeriodSchema, } from '@traceio-org/platform-contracts';
/** Route templates for error reports (PLATFORM_BILLING_API builds the paths). */
const ORGANIZATION = '/v1/admin/billing/organizations/:organizationId';
const ROUTES = {
    planVersions: PLATFORM_BILLING_API.planVersions,
    planVersion: '/v1/admin/billing/plan-versions/:versionId',
    publishPlanVersion: '/v1/admin/billing/plan-versions/:versionId/publish',
    contracts: `${ORGANIZATION}/contracts`,
    contract: `${ORGANIZATION}/contracts/:contractId`,
    endContractTrial: `${ORGANIZATION}/contracts/:contractId/end-trial`,
    usage: `${ORGANIZATION}/usage`,
    lineItems: `${ORGANIZATION}/line-items`,
    adjustments: `${ORGANIZATION}/adjustments`,
    approveAdjustment: `${ORGANIZATION}/adjustments/:adjustmentId/approve`,
    rejectAdjustment: `${ORGANIZATION}/adjustments/:adjustmentId/reject`,
    documents: `${ORGANIZATION}/documents`,
    billingExemption: `${ORGANIZATION}/exemption`,
};
/**
 * Which route and verb each staff billing method serves, so a test can hold the client to the contract's route table
 * (every staff route covered, one method per route and verb, none missing).
 */
export const BILLING_CLIENT_METHODS = {
    adminPlanVersions: { method: 'GET', route: 'planVersions' },
    adminCreatePlanVersionDraft: { method: 'POST', route: 'planVersions' },
    adminPlanVersion: { method: 'GET', route: 'planVersion' },
    adminUpdatePlanVersionDraft: { method: 'PATCH', route: 'planVersion' },
    adminPublishPlanVersion: { method: 'POST', route: 'publishPlanVersion' },
    adminContracts: { method: 'GET', route: 'contracts' },
    adminCreateContract: { method: 'POST', route: 'contracts' },
    adminUpdateContract: { method: 'PATCH', route: 'contract' },
    adminEndContractTrial: { method: 'POST', route: 'endContractTrial' },
    adminBillingUsage: { method: 'GET', route: 'usage' },
    adminBillingLineItems: { method: 'GET', route: 'lineItems' },
    adminAdjustments: { method: 'GET', route: 'adjustments' },
    adminRequestAdjustment: { method: 'POST', route: 'adjustments' },
    adminApproveAdjustment: { method: 'POST', route: 'approveAdjustment' },
    adminRejectAdjustment: { method: 'POST', route: 'rejectAdjustment' },
    adminBillingDocuments: { method: 'GET', route: 'documents' },
    adminSetBillingExemption: { method: 'PUT', route: 'billingExemption' },
};
export function createBillingMethods(request) {
    // Every route here is a staff route: the admin-audience token of the call (RequestOptions.accessToken) or
    // getAccessToken.
    const call = (spec, options) => request({ ...spec, auth: 'bearer', signal: options?.signal, accessToken: options?.accessToken });
    const api = PLATFORM_BILLING_API;
    const periodQuery = (query) => ({
        contractId: query?.contractId,
        periodStart: query?.periodStart,
        cursor: query?.cursor,
        limit: query?.limit,
    });
    return {
        adminPlanVersions: (query, options) => call({
            route: ROUTES.planVersions,
            path: api.planVersions,
            method: 'GET',
            query: {
                productKey: query?.productKey,
                status: query?.status,
                organizationId: query?.organizationId,
            },
            schema: planVersionListSchema,
        }, options),
        adminPlanVersion: (versionId, options) => call({
            route: ROUTES.planVersion,
            path: api.planVersion(versionId),
            method: 'GET',
            schema: planVersionSchema,
        }, options),
        adminCreatePlanVersionDraft: (body, options) => call({
            route: ROUTES.planVersions,
            path: api.planVersions,
            method: 'POST',
            body,
            schema: planVersionSchema,
        }, options),
        adminUpdatePlanVersionDraft: (versionId, body, options) => call({
            route: ROUTES.planVersion,
            path: api.planVersion(versionId),
            method: 'PATCH',
            body,
            schema: planVersionSchema,
        }, options),
        adminPublishPlanVersion: (versionId, body, options) => call({
            route: ROUTES.publishPlanVersion,
            path: api.publishPlanVersion(versionId),
            method: 'POST',
            body,
            schema: planVersionSchema,
        }, options),
        adminContracts: (organizationId, options) => call({
            route: ROUTES.contracts,
            path: api.contracts(organizationId),
            method: 'GET',
            schema: contractListSchema,
        }, options),
        adminCreateContract: (organizationId, body, options) => call({
            route: ROUTES.contracts,
            path: api.contracts(organizationId),
            method: 'POST',
            body,
            schema: contractSchema,
        }, options),
        adminUpdateContract: (organizationId, contractId, body, options) => call({
            route: ROUTES.contract,
            path: api.contract(organizationId, contractId),
            method: 'PATCH',
            body,
            schema: contractSchema,
        }, options),
        adminEndContractTrial: (organizationId, contractId, body, options) => call({
            route: ROUTES.endContractTrial,
            path: api.endContractTrial(organizationId, contractId),
            method: 'POST',
            body,
            schema: contractSchema,
        }, options),
        adminBillingUsage: (organizationId, query, options) => call({
            route: ROUTES.usage,
            path: api.usage(organizationId),
            method: 'GET',
            query: periodQuery(query),
            schema: periodUsageSchema,
        }, options),
        adminBillingLineItems: (organizationId, query, options) => call({
            route: ROUTES.lineItems,
            path: api.lineItems(organizationId),
            method: 'GET',
            query: periodQuery(query),
            schema: ratedPeriodSchema,
        }, options),
        adminAdjustments: (organizationId, options) => call({
            route: ROUTES.adjustments,
            path: api.adjustments(organizationId),
            method: 'GET',
            schema: adjustmentListSchema,
        }, options),
        adminRequestAdjustment: (organizationId, body, options) => call({
            route: ROUTES.adjustments,
            path: api.adjustments(organizationId),
            method: 'POST',
            body,
            schema: adjustmentSchema,
        }, options),
        adminApproveAdjustment: (organizationId, adjustmentId, body, options) => call({
            route: ROUTES.approveAdjustment,
            path: api.approveAdjustment(organizationId, adjustmentId),
            method: 'POST',
            body,
            schema: adjustmentSchema,
        }, options),
        adminRejectAdjustment: (organizationId, adjustmentId, body, options) => call({
            route: ROUTES.rejectAdjustment,
            path: api.rejectAdjustment(organizationId, adjustmentId),
            method: 'POST',
            body,
            schema: adjustmentSchema,
        }, options),
        adminBillingDocuments: (organizationId, options) => call({
            route: ROUTES.documents,
            path: api.documents(organizationId),
            method: 'GET',
            schema: billingDocumentListSchema,
        }, options),
        adminSetBillingExemption: (organizationId, body, options) => call({
            route: ROUTES.billingExemption,
            path: api.billingExemption(organizationId),
            method: 'PUT',
            body,
            schema: billingExemptionSchema,
        }, options),
    };
}
export const INTERNAL_BILLING_CLIENT_METHODS = {
    internalContractSummary: { method: 'GET', route: 'contractSummary' },
    internalChangeCarrierConnections: { method: 'POST', route: 'internalCarrierConnections' },
};
/** Route templates for error reports of the internal methods. */
const INTERNAL_ROUTES = {
    contractSummary: '/v1/internal/billing/organizations/:organizationId/contract-summary',
    internalCarrierConnections: '/v1/internal/billing/organizations/:organizationId/carrier-connections',
};
export function createInternalBillingMethods(request) {
    return {
        internalContractSummary: (organizationId, query, options) => request({
            route: INTERNAL_ROUTES.contractSummary,
            path: PLATFORM_BILLING_API.contractSummary(organizationId),
            method: 'GET',
            query: { productKey: query.productKey },
            auth: 'bearer',
            schema: internalContractSummarySchema,
            signal: options?.signal,
            accessToken: options?.accessToken,
        }),
        internalChangeCarrierConnections: (organizationId, body, options) => request({
            route: INTERNAL_ROUTES.internalCarrierConnections,
            path: PLATFORM_BILLING_API.internalCarrierConnections(organizationId),
            method: 'POST',
            body,
            auth: 'bearer',
            schema: internalContractSummarySchema,
            signal: options?.signal,
            accessToken: options?.accessToken,
        }),
    };
}
//# sourceMappingURL=billing.js.map